import { Dispatch, useEffect, useRef, useState } from "react";
import { PartialNullable } from "../types";

export type UpdatedFields<T> = {
  [P in keyof PartialNullable<T>]?: PartialNullable<T>[P];
};
export type UpdatedFieldsCallback<T> = (prevState: PartialNullable<T>) => {
  [P in keyof PartialNullable<T>]?: PartialNullable<T>[P];
};
export type UpdateFields<T> = (
  fields: UpdatedFields<T> | UpdatedFieldsCallback<T>,
) => void;

export function useAdvancedState<T>(
  initValue?: PartialNullable<T>,
  changeCallback?: (updatedValue: PartialNullable<T>) => void,
): [
  PartialNullable<T>,
  Dispatch<PartialNullable<T>>,
  UpdateFields<T>,
  () => void,
] {
  const initialStateRef = useRef<PartialNullable<T>>(initValue ?? {});
  const [value, setValue] = useState<PartialNullable<T>>(initValue || {});
  const updateState = (updatedFields: {
    [P in keyof PartialNullable<T>]?: PartialNullable<T>[P];
  }) => {
    setValue((prevState) => ({ ...prevState, ...updatedFields }));
  };

  useEffect(() => {
    if (changeCallback) {
      changeCallback(value);
    }
  }, [value]);

  const handleChangeWithCallback = (callback: UpdatedFieldsCallback<T>) => {
    setValue((prevState) => {
      const updatedFields = callback(prevState);
      return {
        ...prevState,
        ...updatedFields,
      };
    });
  };

  const handleEntityChange = (
    props: UpdatedFields<T> | UpdatedFieldsCallback<T>,
  ) => {
    if (props instanceof Function) {
      handleChangeWithCallback(props);
    } else {
      updateState(props);
    }
  };

  const resetState = () => {
    setValue(initialStateRef.current);
  };

  return [value, setValue, handleEntityChange, resetState];
}
